/**
 * Domain Types for Tariff Calculator
 *
 * Frontend-specific types for calculator form inputs, results, and UI state.
 * These types bridge GraphQL types and React component interfaces.
 *
 * @see /frontend/src/types/graphql.ts - GraphQL schema types
 * @see /amplify/data/utils/tariff-calculator.ts - Backend calculation engine
 */

import {
  ConsumptionPatternType
} from './graphql';
import type {
  TariffType as GraphQLTariffType,
  HourlyPattern as GraphQLHourlyPattern
} from './graphql';

// ============================================================================
// BACKWARD COMPATIBLE TYPE EXPORTS (for existing components)
// ============================================================================

/**
 * Household Tariff Type Options (G-series)
 */
export type HouseholdTariffType = 'G11' | 'G12' | 'G12as' | 'G12w' | 'G12n' | 'G12r' | 'G13' | 'G13s' | 'G14';

/**
 * Business Tariff Type Options (C-series)
 */
export type BusinessTariffType = 'C11' | 'C12a' | 'C12b' | 'C21' | 'C22a' | 'C22b';

/**
 * Tariff Type Options (all tariffs including household G and business C)
 * @deprecated Use GraphQLTariffType from './graphql' for new code
 */
export type TariffType = HouseholdTariffType | BusinessTariffType;

/**
 * Consumption Pattern Type
 * Defines user's energy consumption pattern throughout the day
 */
export type ConsumptionPattern =
  | 'UNIFORM'              // Równomierny - identical usage every hour
  | 'MORNINGS_EVENINGS'    // Poranki i wieczory - high usage mornings (6-9) and evenings (17-23)
  | 'WORK_FROM_HOME'       // Praca w domu - high usage during day (9-17)
  | 'SUGGESTED'            // Sugerowany - automatically suggested based on statistical data
  | 'CUSTOM'               // Własny - custom hourly consumption pattern
  | 'FROM_CSV_IMPORT';     // Z importu CSV - use pattern from imported CSV hourly data

/**
 * Hourly Consumption Configuration
 * Defines consumption patterns for weekdays vs. weekends
 */
export interface HourlyConfig {
  weekday: number[];  // 24 hourly values (0.0-1.0, sum should equal 1.0)
  weekend: number[];  // 24 hourly values (0.0-1.0, sum should equal 1.0)
}

/**
 * Calculator Form Values
 */
export interface CalculatorFormValues {
  monthlyConsumption: number[];        // 12 monthly values (Jan-Dec) - REQUIRED
  currentTariff: TariffType;

  // OSD selector (Multi-OSD support)
  selectedOSD?: 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA';

  // Statistical data (Krok 2)
  householdSize: number | '5+';        // 1, 2, 3, 4, or '5+'
  hasHeatPump: boolean;                // Pompa ciepła
  hasSolar: boolean;                   // Fotowoltaika
  hasEV: boolean;                      // Samochód elektryczny

  // Consumption pattern (Krok 3)
  consumptionPattern: ConsumptionPattern;

  // Hourly consumption configuration (Krok 4)
  hourlyConfig?: HourlyConfig;         // Optional hourly breakdown

  // Deprecated: Use monthlyConsumption instead
  annualConsumption?: number;          // kWh/year (for backwards compatibility)
}

/**
 * Tariff Comparison Result
 * Result for a single tariff (G11 or G12)
 */
export interface TariffResult {
  tariffName: string;              // e.g., "G11" or "G12"
  energyCost: number;              // PLN/year (production cost, gross)
  distributionCost: number;        // PLN/year (distribution cost, gross)
  totalAnnualCost: number;         // PLN/year (total, gross)
  savings: number;                 // PLN/year (positive = saves money, negative = loses money)
  averageCostPerKwh: number;       // PLN/kWh (gross)
  isRecommended: boolean;          // True if this is the cheaper option
}

/**
 * Calculation Results
 * Complete comparison of selected tariffs (2-9 tariffs)
 * Multi-OSD Support: Includes G12n (PGE) and G12r (Energa)
 */
export interface CalculationResults {
  g11?: TariffResult;
  g12?: TariffResult;
  g12as?: TariffResult;
  g12w?: TariffResult;
  g12n?: TariffResult;             // PGE-specific (Nocna)
  g12r?: TariffResult;             // Energa-specific (Rozszerzona weekendowa)
  g13?: TariffResult;
  g13s?: TariffResult;
  recommendedTariff: TariffType;
  potentialSavings: number;        // PLN/year (absolute savings)
  savingsPercent: number;          // Percentage savings
}

/**
 * Calculator State
 */
export interface CalculatorState {
  formValues: CalculatorFormValues | null;
  results: CalculationResults | null;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// EXTENDED TYPES FOR PHASE 1 MVP
// ============================================================================

/**
 * Extended Calculator Input
 * Complete form data for advanced calculator features
 */
export interface ExtendedCalculatorInput {
  // Required fields
  annualConsumption: number;           // Total kWh per year
  currentTariff: GraphQLTariffType;    // User's current tariff

  // Optional fields
  distributor?: string;                // Default: "TAURON" (Phase 1)
  voivodeship?: string;                // User's region
  consumptionPattern?: ConsumptionPatternType; // Default: UNIFORM
  monthlyConsumption?: number[];       // 12 monthly values (Jan-Dec)
  hourlyPattern?: GraphQLHourlyPattern; // Custom hourly pattern

  // Household characteristics
  householdSize?: number;              // Number of people (1-10)
  hasHeatPump?: boolean;
  hasSolarPanels?: boolean;
  hasElectricVehicle?: boolean;
}

/**
 * Monthly Comparison Result
 * Month-by-month comparison of all tariffs
 */
export interface MonthlyComparisonResult {
  month: number;                       // Month number (1-12)
  monthName: string;                   // Display name (e.g., "Styczeń")
  consumption: number;                 // Total kWh for month
  g11Cost: number;                     // G11 total cost (PLN, gross)
  g12Cost: number;                     // G12 total cost (PLN, gross)
  savings: number;                     // Savings of best option (PLN)
  bestTariff: GraphQLTariffType;       // Best tariff for this month
  g11Details?: MonthlyTariffDetails;
  g12Details?: MonthlyTariffDetails;
}

/**
 * Monthly Tariff Details
 * Detailed breakdown for a specific tariff in a month
 */
export interface MonthlyTariffDetails {
  totalKwh: number;
  productionCost: number;              // PLN (net)
  distributionCost: number;            // PLN (net)
  totalCost: number;                   // PLN (gross)
  dayKwh?: number;                     // G12 only
  nightKwh?: number;                   // G12 only
}

/**
 * Annual Tariff Summary
 * Annual cost summary for a specific tariff
 */
export interface AnnualTariffSummary {
  tariff: GraphQLTariffType;
  totalCost: number;                   // PLN (gross)
  totalKwh: number;
  productionCost: number;              // PLN (net)
  distributionCost: number;            // PLN (net)
  avgCostPerKwh: number;               // PLN/kWh (gross)
  savingsVsG11?: number;               // PLN (only for G12)
  costBreakdown: CostBreakdown;
}

/**
 * Cost Breakdown
 * Percentage breakdown of costs
 */
export interface CostBreakdown {
  productionPercent: number;           // % of total
  distributionPercent: number;         // % of total
  fixedFeePercent: number;             // % of total
}

/**
 * Extended Tariff Comparison Result
 * Complete comparison of all tariffs with detailed breakdowns
 * Multi-OSD Support: Includes G12n (PGE) and G12r (Energa)
 */
export interface ExtendedTariffComparisonResult {
  g11: AnnualTariffSummary;
  g12: AnnualTariffSummary;
  g12as?: AnnualTariffSummary;         // Optional (TAURON, ENEA, ENERGA)
  g12w?: AnnualTariffSummary;          // Optional (all OSD)
  g12n?: AnnualTariffSummary;          // Optional (PGE-specific: Nocna)
  g12r?: AnnualTariffSummary;          // Optional (Energa-specific: Rozszerzona weekendowa)
  g13?: AnnualTariffSummary;           // Optional (Tauron only)
  g13s?: AnnualTariffSummary;          // Optional (Tauron only)
  recommended: GraphQLTariffType;
  potentialSavings: number;            // PLN/year (gross)
  savingsPercent: number;              // Percentage savings
  savingsVsG11: number;                // PLN/year (gross)
  breakEvenMonth?: number;             // Month when switching breaks even (if applicable)
}

/**
 * Extended Calculation Result
 * Complete result with monthly and annual comparisons
 */
export interface ExtendedCalculationResult {
  input: ExtendedCalculatorInput;
  comparison: ExtendedTariffComparisonResult;
  monthlyResults: MonthlyComparisonResult[];
  calculatedAt: Date;
  calculationId?: string;              // DynamoDB ID if saved
}

// ============================================================================
// PRICING DATA TYPES
// ============================================================================

/**
 * Simplified Tariff Pricing
 * Frontend-friendly pricing structure extracted from DistributorPricing
 */
export interface TariffPricing {
  tariffCode: GraphQLTariffType;
  tariffName: string;
  distributor: string;

  // Energy prices (net, PLN/kWh)
  energyPrices: {
    allDay?: number;                   // G11 only
    day?: number;                      // G12, G13
    night?: number;                    // G12, G13
    peak?: number;                     // G13 only
  };

  // Distribution prices (net, PLN/kWh)
  distributionPrices: {
    allDay?: number;                   // G11 only
    day?: number;                      // G12, G13
    night?: number;                    // G12, G13
    peak?: number;                     // G13 only
  };

  // Fixed fees (net, PLN/month)
  fixedFees: {
    singlePhase: number;
    threePhase: number;
  };

  // Additional charges (net, PLN/kWh)
  qualityCharge: number;

  // Time zones
  timeZones: {
    dayStart?: number;                 // Hour (0-23), G12/G13 only
    dayEnd?: number;                   // Hour (0-23), G12/G13 only
    nightStart?: number;               // Hour (0-23), G12/G13 only
    nightEnd?: number;                 // Hour (0-23), G12/G13 only
  };

  // Metadata
  validFrom: Date;
  validTo: Date;
  isActive: boolean;
}

/**
 * Distributor Pricing Set
 * Complete pricing data for a distributor
 */
export interface DistributorPricingSet {
  distributorCode: string;
  distributorName: string;
  tariffs: {
    G11?: TariffPricing;
    G12?: TariffPricing;
    G12as?: TariffPricing;
    G12w?: TariffPricing;
    G13?: TariffPricing;
    G13s?: TariffPricing;
  };
  validFrom: Date;
  validTo: Date;
}

// ============================================================================
// UI STATE TYPES
// ============================================================================

/**
 * Calculator UI State
 * State management for calculator component
 */
export interface CalculatorUIState {
  isLoading: boolean;
  isCalculating: boolean;
  isSaving: boolean;
  error?: string;
  result?: ExtendedCalculationResult;
  pricingData?: DistributorPricingSet;
}

/**
 * Chart Data Point
 * Data point for chart visualizations
 */
export interface ChartDataPoint {
  month: string;                       // Month name
  g11: number;                         // G11 cost
  g12: number;                         // G12 cost
  savings: number;                     // Savings amount
}

/**
 * Consumption Pattern Preset
 * Predefined consumption pattern with metadata
 */
export interface ConsumptionPatternPreset {
  id: ConsumptionPatternType;
  name: string;
  description: string;
  icon: string;                        // Icon identifier
  hourlyPattern: GraphQLHourlyPattern;
  suitableFor: string[];               // List of suitable use cases
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Month Name Mapping (Polish)
 * Polish month names for display
 */
export const MONTH_NAMES_PL: Record<number, string> = {
  1: 'Styczeń',
  2: 'Luty',
  3: 'Marzec',
  4: 'Kwiecień',
  5: 'Maj',
  6: 'Czerwiec',
  7: 'Lipiec',
  8: 'Sierpień',
  9: 'Wrzesień',
  10: 'Październik',
  11: 'Listopad',
  12: 'Grudzień'
};

/**
 * Month Name Mapping (Short, Polish)
 * Short Polish month names for charts
 */
export const MONTH_NAMES_SHORT_PL: Record<number, string> = {
  1: 'Sty',
  2: 'Lut',
  3: 'Mar',
  4: 'Kwi',
  5: 'Maj',
  6: 'Cze',
  7: 'Lip',
  8: 'Sie',
  9: 'Wrz',
  10: 'Paź',
  11: 'Lis',
  12: 'Gru'
};

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for valid tariff type
 */
export function isValidTariffType(value: unknown): value is TariffType {
  return typeof value === 'string' && ['G11', 'G12', 'G12as', 'G12w', 'G12n', 'G12r', 'G13', 'G13s', 'G14'].includes(value);
}

/**
 * Type guard for valid consumption pattern
 */
export function isValidConsumptionPattern(value: unknown): value is ConsumptionPatternType {
  return typeof value === 'string' && Object.values(ConsumptionPatternType).includes(value as ConsumptionPatternType);
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate annual consumption
 */
export function validateAnnualConsumption(value: number): string | undefined {
  if (value < 0) {return 'Roczne zużycie nie może być ujemne';}
  if (value === 0) {return 'Roczne zużycie musi być większe niż 0';}
  if (value > 100000) {return 'Roczne zużycie wydaje się nieprawdopodobnie wysokie';}
  return undefined;
}

/**
 * Validate monthly consumption array
 */
export function validateMonthlyConsumption(values: number[]): string | undefined {
  if (values.length !== 12) {return 'Wymagane jest 12 miesięcznych wartości';}
  if (values.some(v => v < 0)) {return 'Miesięczne zużycie nie może być ujemne';}
  if (values.every(v => v === 0)) {return 'Co najmniej jeden miesiąc musi mieć zużycie';}
  return undefined;
}

/**
 * Validate hourly pattern
 */
export function validateHourlyPattern(pattern: GraphQLHourlyPattern): string | undefined {
  if (pattern.weekday.length !== 24) {return 'Wzorzec weekday musi mieć 24 godziny';}
  if (pattern.weekend.length !== 24) {return 'Wzorzec weekend musi mieć 24 godziny';}
  if (pattern.weekday.some(v => v < 0)) {return 'Wartości wzorca nie mogą być ujemne';}
  if (pattern.weekend.some(v => v < 0)) {return 'Wartości wzorca nie mogą być ujemne';}
  return undefined;
}
